/**
 * Object of standard events that can be supported by adapters. Those not supported by the adapter
 * can optionally be handled by plugins instead.
 * @private
 */
const CORE_EVENTS = {
  media: [
    'canplay',
    'canplaythrough',
    'durationchange',
    'emptied',
    'ended',
    'loadeddata',
    'loadedmetadata',
    'pause',
    'play',
    'playing',
    'progress',
    'ratechange',
    'seeked',
    'seeking',
    'stalled',
    'suspend',
    'timeupdate',
    'volumechange',
    'waiting',
    'retry',
    /* events not originating from the HTMLMediaElement */
    'retry_resolved',
    'retry_timeout',
    'abort',
    'error',
    'loadstart',

    'finished',
    'segmentnotfound',
    'timedmetadata',
  ],
  yospace: ['emsg', 'id3'],
  player: ['loading', 'shutdown', 'destroy'],
  ad: [
    'begin',
    'end',
    'breakbegin',
    'breakend',
    'error',
    'skip',
    'adblock',
    'pause',
    'play',
    'buffering',
    'timeupdate',
    'playing',
  ],
  network: ['connectivitychange', 'offline'],
  abr: ['adaptation'],
  drm: ['error'],
  view: [
    'sourcechange',
    'fullscreenchange',
    'inlinecontenttoggle',
    'error',
    'subtitleschange',
    'addtrack',
    'removetrack',
    'metadataupdated',
    'pause',
    'play',
    'escape',
    'error_overlay_closed',
    'adbreakend',
    'adbreakbegin',
    'adpause',
    'adplay',
    'adbuffering',
  ],
  live: ['ending', 'ended'],
  scte: ['timedevents'],
  seekable: ['updated'],
  startOver: ['availability'],
  concurrency: ['unlock'],
} as const;

export default CORE_EVENTS;

export const WORKER_EVENTS: UnifiedPlayerEvents[] = [
  'seekable.updated',
  'scte.timedevents',
  'ad.breakbegin',
  'ad.breakend',
];

type CoreEventsMappedType = {
  [Namespace in keyof typeof CORE_EVENTS]: `${Namespace}.${(typeof CORE_EVENTS)[Namespace][number]}`;
};
export type UnifiedPlayerEvents =
  | CoreEventsMappedType[keyof CoreEventsMappedType]
  | 'view.adapter.player.stats'
  | 'plugin.error';

/**
 * Sent when media (live) has events for the future, mainly used when receiving information about commercials. (dash).
 * @event "yospace.emsg"
 * @see {@link https://www.w3.org/TR/media-timed-events/}
 * @property {string} type `yospace.emsg`.
 */
export const YOSPACE_EMSG = 'yospace.emsg' as const;

/**
 * See yospace.emsg - This is the equivalent from HLS.
 * @event "yospace.id3"
 * @see {@link https://www.w3.org/TR/media-timed-events/}
 * @property {string} type `yospace.id3`.
 */
export const YOSPACE_ID3 = 'yospace.id3' as const satisfies UnifiedPlayerEvents;

/**
 * Sent when enough data is available that the media can be played, at least for a couple of frames.
 * This corresponds to the HAVE_FUTURE_DATA readyState.
 * @event "media.canplay"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/canplay}
 * @property {string} type `media.canplay`.
 */
export const MEDIA_CANPLAY: UnifiedPlayerEvents = 'media.canplay';

/**
 * Sent when the ready state changes to HAVE_ENOUGH_DATA, indicating that the entire media can be
 * played without interruption, assuming the download rate remains at least at the current level.
 * It will also be fired when playback is toggled between paused and playing.
 * @event "media.canplaythrough"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/canplaythrough}
 * @property {string} type `media.canplaythrough`.
 */
export const MEDIA_CANPLAYTHROUGH: UnifiedPlayerEvents = 'media.canplaythrough';

/**
 * The metadata has loaded or changed, indicating a change in duration of the media. This is sent,
 * for example, when the media has loaded enough that the duration is known.
 * @event "media.durationchange"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/durationchange}
 * @property {string} type `media.durationchange`.
 */
export const MEDIA_DURATIONCHANGE: UnifiedPlayerEvents = 'media.durationchange';

/**
 * Sent when playback completes.
 * @event "media.ended"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/ended}
 * @property {string} type `media.ended`.
 */
export const MEDIA_ENDED = 'media.ended';
export const MEDIA_FINISHED: UnifiedPlayerEvents = 'media.finished';

/**
 * Sent when a media error occurs.
 * @event "media.error"
 * @property {string} type `media.error`.
 */
export const MEDIA_ERROR: UnifiedPlayerEvents = 'media.error';

/**
 * The first frame of the media has finished loading.
 * @event "media.loadeddata"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/loadeddata}
 * @property {string} type `media.loadeddata`.
 */
export const MEDIA_LOADEDDATA: UnifiedPlayerEvents = 'media.loadeddata';

/**
 * Sent when loading of the media begins.
 * @event "media.loadstart"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/loadstart}
 * @property {string} type `media.loadstart`.
 */
export const MEDIA_LOADSTART: UnifiedPlayerEvents = 'media.loadstart';

/**
 * The media's metadata has finished loading; all attributes now contain as much useful information
 * as they're going to.
 * @event "media.loadedmetadata"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/loadedmetadata}
 * @property {string} type `media.loadedmetadata`.
 */
export const MEDIA_LOADEDMETADATA = 'media.loadedmetadata';

/**
 * Sent when playback is paused.
 * @event "media.pause"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/pause}
 * @property {string} type `media.pause`.
 */
export const MEDIA_PAUSE = 'media.pause';

/**
 * Sent when playback of the media starts.
 * @event "media.play"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/play}
 * @property {string} type `media.play`.
 */
export const MEDIA_PLAY = 'media.play';

/**
 * Sent when the media begins to play (either for the first time, after having been paused, or after
 * ending and then restarting).
 * @event "media.playing"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/playing}
 * @property {string} type `media.playing`.
 */
export const MEDIA_PLAYING: UnifiedPlayerEvents = 'media.playing';

/**
 * Sent periodically to inform interested parties of progress downloading the media.
 * @event "media.progress"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/progress}
 * @property {string} type `media.progress`.
 */
export const MEDIA_PROGRESS: UnifiedPlayerEvents = 'media.progress';

/**
 * Sent when the playback speed changes.
 * @event "media.ratechange"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/ratechange}
 * @property {string} type `media.ratechange`.
 */
export const MEDIA_RATECHANGE: UnifiedPlayerEvents = 'media.ratechange';

/**
 * Sent when a seek operation completes.
 * @event "media.seeked"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/seeked}
 * @property {string} type `media.seeked`.
 */
export const MEDIA_SEEKED = 'media.seeked';

/**
 * Sent when a seek operation begins.
 * @event "media.seeking"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/seeking}
 * @property {string} type `media.seeking`.
 */
export const MEDIA_SEEKING: UnifiedPlayerEvents = 'media.seeking';

/**
 * The time indicated by the player's currentTime attribute has changed.
 * @event "media.timeupdate"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/timeupdate}
 * @property {string} type `media.timeupdate`.
 */
export const MEDIA_TIMEUPDATE = 'media.timeupdate';

/**
 * Sent when the audio volume changes (both when the volume is set and when muted).
 * @event "media.volumechange"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/volumechange}
 * @property {string} type `media.volumechange`.
 */
export const MEDIA_VOLUMECHANGE = 'media.volumechange';

/**
 * Sent when the requested operation (such as playback) is delayed pending the completion of another
 * operation (such as a seek).
 * @event "media.waiting"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/waiting}
 * @property {string} type `media.waiting`.
 */
export const MEDIA_WAITING = 'media.waiting' as const;
export const MEDIA_STALLED: UnifiedPlayerEvents = 'media.stalled';
export const MEDIA_RETRY: UnifiedPlayerEvents = 'media.retry';
export const MEDIA_RETRY_TIMEOUT: UnifiedPlayerEvents = 'media.retry_timeout';
export const MEDIA_RETRY_RESOLVED: UnifiedPlayerEvents = 'media.retry_resolved';
export const VIEW_ERROR_OVERLAY_CLOSED: UnifiedPlayerEvents = 'view.error_overlay_closed';
export const MEDIA_CONTINUE = 'media.continue' as const;

/**
 * The emptied event is fired when the media has become empty; for example,
 * this event is sent if the media has already been loaded (or partially loaded),
 * and the load() method is called to reload it.
 * @event "media.emptied"
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Events/emptied}
 * @property {string} type `media.emptied`.
 */
export const MEDIA_EMPTIED: UnifiedPlayerEvents = 'media.emptied';

/**
 * Sent when a user under an ad has triggered play.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.play"
 * @property {string} type `ad.play`.
 */
export const AD_PLAY: UnifiedPlayerEvents = 'ad.play';

/**
 * Sent when a user under an ad has triggered pause.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.pause"
 * @property {string} type `ad.pause`.
 */
export const AD_PAUSE = 'ad.pause';

/**
 * Fired when the ad has stalled playback to buffer.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.buffering"
 * @property {string} type `ad.buffering`.
 */
export const AD_BUFFERING = 'ad.buffering';

/**
 * Sent when an ad has begun playing.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.begin"
 * @property {string} type `ad.begin`.
 */
export const AD_BEGIN = 'ad.begin';

/**
 * Sent when an ad has ended.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.end"
 * @property {string} type `ad.end`.
 */
export const AD_END = 'ad.end';

/**
 * Sent when an ad break has begun playing.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.breakbegin"
 * @property {string} type `ad.breakbegin`.
 */
export const AD_BREAKBEGIN = 'ad.breakbegin';

/**
 * Sent when an ad break has ended.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.breakend"
 * @property {string} type `ad.breakend`.
 */
export const AD_BREAKEND = 'ad.breakend';

/**
 * Sent if an error occurs during ad playback.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.error"
 * @property {string} type `ad.error`.
 */
export const AD_ERROR: UnifiedPlayerEvents = 'ad.error';

/**
 * Sent if a skippable ad was skipped by the user.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.skip"
 * @property {string} type `ad.skip`.
 */
export const AD_SKIP: UnifiedPlayerEvents = 'ad.skip';

/**
 * Sent if an ad was blocked.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.adblock"
 * @property {string} type `ad.adblock`.
 */
export const AD_ADBLOCK: UnifiedPlayerEvents = 'ad.adblock';

/**
 * The time for the currently playing ad has changed.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "ad.timeupdate"
 * @property {string} type `ad.timeupdate`.
 */
export const AD_TIMEUPDATE = 'ad.timeupdate';

/**
 * Sent when the network connection changes between online and offline.
 * @note Requires {@link external:NetworkPlugin}
 * @event "network.connectivitychange"
 * @property {string}  type        `network.connectivitychange`.
 * @property {boolean} offline     True when player is offline, otherwise false.
 */
export const NETWORK_CONNECTIVITYCHANGE: UnifiedPlayerEvents = 'network.connectivitychange';

/**
 * Sent when the network connection changes between online and offline.
 * @event "network.offline"
 * @property {string}  type        `network.offline`.
 */
export const NETWORK_OFFLINE: UnifiedPlayerEvents = 'network.offline';

/**
 * Fired when an automatic adaptation causes the active tracks to change.
 * @note Not currently supported by any implementation
 * @event "abr.adaptation"
 * @property {string} type `abr.adaptation`.
 */
export const ABR_ADAPTATION = 'abr.adaptation';

/**
 * Sent if an error occurs during handling of protected content.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "drm.error"
 * @property {string} type `drm.error`.
 */
export const DRM_ERROR: UnifiedPlayerEvents = 'drm.error';

/**
 * Sent when the source has been changed by the adapter. Can be used in plugins to delay logic
 * that depends on reading media properties for the new media.
 * @event "view.sourcechange"
 * @property {string} type `view.sourcechange`.
 */
export const VIEW_SOURCECHANGE = 'view.sourcechange';

/**
 * Sent when the metadata has been updated.
 * @event "view.metadataupdated"
 * @property {string} type `view.metadataupdated`.
 */
export const VIEW_METADATAUPDATED: UnifiedPlayerEvents = 'view.metadataupdated';

/**
 * Sent when player enters or leaves fullscreen.
 * @event "view.fullscreenchange"
 * @property {string}  type       `view.fullscreenchange`.
 * @property {boolean} fullscreen True if player is in fullscreen, false otherwise.
 */
export const VIEW_FULLSCREENCHANGE: UnifiedPlayerEvents = 'view.fullscreenchange';

/**
 * Sent when player toggles subtitles.
 * @event "view.subtitleschange"
 * @property {string}  type       `view.subtitleschange`.
 * @property {boolean} caption    True if player has enabled subtitles, false otherwise.
 */
export const VIEW_SUBTITLESCHANGE: UnifiedPlayerEvents = 'view.subtitleschange';

/**
 * Sent when text track is added.
 * @event "view.addtrack"
 */
export const VIEW_TRACKADD: UnifiedPlayerEvents = 'view.addtrack';

/**
 * Sent when a text track is removed
 * @event "view.removetrack"
 */
export const VIEW_TRACKREMOVE: UnifiedPlayerEvents = 'view.removetrack';

/**
 * Sent when the player's inline content button is toggled.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "view.inlinecontenttoggle"
 * @property {string} type `view.inlinecontenttoggle`.
 */
export const VIEW_INLINECONTENTTOGGLE: UnifiedPlayerEvents = 'view.inlinecontenttoggle';

/**
 * Sent when the user pauses player, only used for triggering GUI to toggle play/pause icons.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "view.pause"
 * @property {string} type `view.pause`.
 */
export const VIEW_PAUSE: UnifiedPlayerEvents = 'view.pause';

/**
 * Sent when the user unpauses player, only used for triggering GUI to toggle play/pause icons.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "view.play"
 * @property {string} type `view.play`.
 */
export const VIEW_PLAY: UnifiedPlayerEvents = 'view.play';

/**
 * Sent when an error occurs in the adapter during playback.
 * @note Requires {@link external:TheoPlayerAdapter}
 * @event "view.error"
 * @property {string} type `view.error`.
 */
export const VIEW_ERROR: UnifiedPlayerEvents = 'view.error';

export const VIEW_AD_BREAKEND = 'view.adbreakend';
export const VIEW_AD_BREAKBEGIN = 'view.adbreakbegin';
export const VIEW_AD_PAUSE = 'view.adpause';
export const VIEW_AD_PLAY = 'view.adplay';
export const VIEW_AD_BUFFERING = 'view.adbuffering';

/**
 * Sent if a plugin error occurred.
 * @event "plugin.error"
 * @property {string}         type  `plugin.error`.
 * @property {external:Error} error Error object containing more information.
 */
export const PLUGIN_ERROR: UnifiedPlayerEvents = 'plugin.error';

/**
 * Emitted X time before live-event is ending, with expected time of end
 * @event "live.ending"
 * @property {string} type "live.ending"
 */
export const LIVE_ENDING: UnifiedPlayerEvents = 'live.ending';

/**
 * Emitted when live-event ends.
 * @event "live.end"
 * @property {string} type "live.end"
 */
export const LIVE_ENDED: UnifiedPlayerEvents = 'live.ended';

/**
 * Emitted when entering a timed SCTE-35 event.
 * @event "scte.timedevents"
 * @see {@link https://www.w3.org/TR/media-timed-events/}
 * @property {string} type "scte.timedevents"
 */
export const SCTE_TIMEDEVENTS = 'scte.timedevents';

export const PLAYER_LOADING: UnifiedPlayerEvents = 'player.loading';

/**
 * Event dispatched by cast.framework.CastReceiverContext when the application is shutdown.
 */
export const PLAYER_SHUTDOWN: UnifiedPlayerEvents = 'player.shutdown';

/**
 * Emitted by the player when it is destoryed.
 */
export const PLAYER_DESTROY: UnifiedPlayerEvents = 'player.destroy';

// Emitted when user triggers an Concurrency unlock
export const CONCURRENCY_UNLOCK: UnifiedPlayerEvents = 'concurrency.unlock';
